import { IUserDepricated } from "@mctypes/global.types";
import { ColDef } from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { Box, Flex, Progress, Switch, Tooltip } from "@mantine/core";
import React, { useCallback, useMemo, useState } from "react";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import {
  FailedAlert,
  HSpace,
  McInputNew,
  MCLoader,
  SegmentedToggle,
  TitleWithValue,
  VSpace,
} from "../../../../components";
import { useAppSelector } from "../../../../hooks";
import {
  useGetNrhRoomValidationCommentsQuery,
  useGetNrhRoomValidationsQuery,
  useUpsertNrhRoomValidationCommentMutation,
} from "../../admin/mcbim/McBimMongoRTK";
import ArkSingleRoomValidationTable from "./ArkSingleRoomValidationTable";
import RIESingleRoomValidationTable from "./RIESingleRoomValidationTable";
import { INrhRoomValidationRow } from "./nrhRoomValTypes";

const NRHRoomVal = () => {
  const [onlyFunctionRooms, setOnlyFunctionRooms] = useState(true);

  const LOCAL_STORAGE_DISCIPLINE_KEY = "nrhroom_valdiscipline";

  const getInitialDiscipline = () => {
    const savedDiscipline = localStorage.getItem(LOCAL_STORAGE_DISCIPLINE_KEY);
    return savedDiscipline === "ARK" || savedDiscipline === "RIE" ? savedDiscipline : "RIE";
  };

  const [discipline, setDiscipline] = React.useState<"ARK" | "RIE">(getInitialDiscipline);

  const {
    data: roomValidations = [],
    isLoading: loadingValidations,
    isError: errorLoadingValidations,
  } = useGetNrhRoomValidationsQuery({ projectId: "5f6085966d375136b9835f7a" });

  const {
    data: comments = [],
    isLoading: isLoadingComments,
    isError: isErrorComments,
  } = useGetNrhRoomValidationCommentsQuery({ projectId: "5f6085966d375136b9835f7a" });

  const [upsertComment, { isLoading: isUpsertingComment, isError: errorUpsertingComment }] =
    useUpsertNrhRoomValidationCommentMutation();

  const isFunctionRoom = (roomNr: string): boolean => {
    if (roomNr.startsWith("8")) {
      return false;
    }
    if (roomNr.startsWith("9")) {
      return false;
    }
    return true;
  };

  const rows = useMemo(() => {
    return roomValidations
      .filter((room) => {
        if (onlyFunctionRooms === false) {
          return true;
        }
        if (onlyFunctionRooms && isFunctionRoom(room.roomNr)) {
          return true;
        }
        return false;
      })
      .map((row) => {
        const comment = comments.find((comment) => comment.roomNr === row.roomNr)?.comment || "";
        return {
          ...row,
          RIE: {
            ...row.RIE,
            comment,
          },
        };
      });
  }, [roomValidations, comments, onlyFunctionRooms]);

  const user: IUserDepricated = useAppSelector((state) => state.globalReducer.user);

  const columns: ColDef<INrhRoomValidationRow>[] = [
    {
      headerName: "",
      cellRenderer: (params) => {
        return params.node.expanded ? <i className="fa fa-caret-up" /> : <i className="fa fa-caret-down" />;
      },
      width: 50,
    },
    {
      field: "roomNr",
      headerName: "Romnr",
      filter: "agTextColumnFilter",
      sort: "asc",
    },
    {
      field: "roomName",
      headerName: "Romnavn",
      filter: true,
    },
    {
      field: "floor",
      headerName: "Etasje",
      filter: true,
    },
    {
      field: "wing",
      headerName: "Lamell",
      filter: true,
    },
    {
      field: "mmi",
      headerName: "MMI",
      filter: true,
    },
    {
      field: "standardRoomNr",
      headerName: "Std romnr",
      hide: discipline !== "RIE",
      filter: true,
    },
    {
      field: "mmiStandardRum",
      headerName: "MMI Std rom",
      hide: discipline !== "RIE",
      filter: true,
    },
    {
      field: "RIE.nrOfCorrectTypes",
      headerName: "nrOfCorrectTypes",
      hide: true,
    },
    {
      field: "RIE.nrOfTypes",
      headerName: "nrOfTypes",
      hide: true,
    },
    {
      field: "RIE.percentageCorrect",
      hide: discipline !== "RIE",
      headerName: "Matchende artikler %",
      cellRenderer: (params) => {
        const value = Math.max(0, Math.min(100, Number(params.value ?? 0)));
        const displayVal = `${Math.round(params.value)}%`;
        return (
          <Box w="100%">
            <Progress.Root size={20} radius={4}>
              <Progress.Section
                value={value}
                color={params.data.nrOfCorrectTypes === params.data.nrOfTypes ? "green" : "yellow"}
              >
                <Progress.Label>{displayVal}</Progress.Label>
              </Progress.Section>
            </Progress.Root>
          </Box>
        );
      },
    },
    {
      hide: discipline !== "RIE",
      field: "RIE.comment",
      filter: "agTextColumnFilter",
      autoHeight: true,
      headerName: `Kommentar RIE`,
      cellClass: "cell-p0",
      minWidth: 200,
      flex: 1,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <Box mih="35px">
            <TitleWithValue
              wrap={true}
              type="textarea"
              editable={true}
              allowEmpty
              title=""
              value={row.RIE?.comment}
              saveCb={(oldValue: string, newValue: string) =>
                upsertComment({
                  projectId: "5f6085966d375136b9835f7a",
                  roomNr: row.roomNr,
                  comment: newValue,
                  userName: user?.name || "Unknown",
                  createdAt: new Date(),
                })
              }
            />
          </Box>
        );
      },
    },
    {
      field: "ARK.percentageCorrect",
      hide: discipline !== "ARK",
      headerName: "Matchende artikler %",
      cellRenderer: (params) => {
        const value = Math.max(0, Math.min(100, Number(params.value ?? 0)));
        const displayVal = `${Math.round(params.value)}%`;
        return (
          <Box w="100%">
            <Progress.Root size={20} radius={4}>
              <Progress.Section
                value={value}
                color={params.data.nrOfCorrectTypes === params.data.nrOfTypes ? "green" : "yellow"}
              >
                <Progress.Label>{displayVal}</Progress.Label>
              </Progress.Section>
            </Progress.Root>
          </Box>
        );
      },
    },
  ];

  const DetailCellRendererRIE = ({ data, node, api }: CustomCellRendererProps) => {
    return <RIESingleRoomValidationTable devices={data.RIE.devicesRIECompare} />;
  };

  const DetailCellRendererARK = ({ data, node, api }: CustomCellRendererProps) => {
    return <ArkSingleRoomValidationTable devices={data.ARK.devicesARKCompare} />;
  };

  const onRowClicked = (params) => {
    if (params.event.defaultPrevented) {
      return null;
    }
    params.node.setExpanded(!params.node.expanded);
    params.api.refreshCells();
  };

  const isLoading = loadingValidations || isLoadingComments || isUpsertingComment;
  const isError = errorLoadingValidations || isErrorComments || errorUpsertingComment;
  const detailCellRenderer =
    discipline === "RIE"
      ? useCallback(DetailCellRendererRIE, [discipline])
      : useCallback(DetailCellRendererARK, [discipline]);

  const _setDisciplineAndLocalStorage = (discipline: "ARK" | "RIE") => {
    setDiscipline(discipline);
    localStorage.setItem(LOCAL_STORAGE_DISCIPLINE_KEY, discipline);
  };

  const [searchText, setSearchText] = useState("");

  return (
    <Flex direction="column" h="100%" w="100%">
      <div>
        <Box w="180px" maw="100%">
          <SegmentedToggle
            value={discipline}
            onChange={(nextDiscipline) => _setDisciplineAndLocalStorage(nextDiscipline as "ARK" | "RIE")}
            options={[
              { value: "ARK", label: "ARK" },
              { value: "RIE", label: "RIE" },
            ]}
          />
        </Box>
      </div>
      <Box className="border" p="0.25rem" style={{ display: "flex" }}>
        <Box w="300px">
          <McInputNew
            type="text"
            placeholder="Søk..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            my="0.5rem"
          />
        </Box>
        <Flex p={0} mt="0.5rem">
          <p style={{ marginRight: "0.5rem" }}>
            <Tooltip label="Vis kun funktionsrom (rom som ikke startar på 8 eller 9)" position="top" withArrow>
              <span>
                <i className="fa fa-info-circle fa-fw icon-info" />
              </span>
            </Tooltip>
            Funksjonsrom
          </p>
          <HSpace />
          <Switch
            size="sm"
            checked={onlyFunctionRooms}
            onChange={(event) => setOnlyFunctionRooms(event.currentTarget.checked)}
          />
        </Flex>
        <HSpace />
      </Box>

      {isLoading && <MCLoader />}
      {isError && (
        <div>
          <FailedAlert />
        </div>
      )}
      <Box className="border ag-theme-alpine" h="100%" p="0.5rem" style={{ overflow: "auto" }} w="100%">
        <VSpace />
        <AgGridReact
          detailCellRenderer={detailCellRenderer}
          rowData={rows}
          columnDefs={columns}
          masterDetail={true}
          onRowClicked={onRowClicked}
          localeText={AG_GRID_LOCALE_NOR}
          quickFilterText={searchText}
        />
      </Box>
    </Flex>
  );
};

export default NRHRoomVal;
